<template>
  <div class="dashboard-preview" data-testid="dashboard-preview">
    <div class="d-flex flex-wrap align-items-center gap-2 mb-4">
      <h2 class="font-serif mb-0">{{ $t("billing.preview.title") }}</h2>
      <span class="badge badge-phoenix badge-phoenix-secondary">
        {{ $t("billing.preview.sample") }}
      </span>
    </div>

    <div class="row g-3 mb-4">
      <div v-for="kpi in kpis" :key="kpi.key" class="col-6 col-xl-3">
        <div class="card h-100">
          <div class="card-body">
            <div
              class="d-flex align-items-center gap-2 text-body-tertiary mb-2"
            >
              <FontAwesomeIcon :icon="kpi.icon" />
              <span class="fs-9 text-uppercase fw-semibold">
                {{ $t(`billing.preview.kpi.${kpi.key}`) }}
              </span>
            </div>
            <div class="fs-5 fw-bold">{{ kpi.value }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mb-4">
      <div class="col-12 col-xl-8">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="mb-3">{{ $t("billing.preview.coverage_title") }}</h5>
            <div class="chart-box">
              <canvas ref="coverageCanvas"></canvas>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-xl-4">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="mb-3">{{ $t("billing.preview.technology_title") }}</h5>
            <div class="chart-box">
              <canvas ref="technologyCanvas"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12 col-xl-7">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="mb-3">{{ $t("billing.preview.certificates_title") }}</h5>
            <div class="table-responsive">
              <table class="table table-sm fs-9 mb-0">
                <thead>
                  <tr>
                    <th>{{ $t("billing.preview.columns.country") }}</th>
                    <th>{{ $t("billing.preview.columns.technology") }}</th>
                    <th>{{ $t("billing.preview.columns.vintage") }}</th>
                    <th class="text-end">
                      {{ $t("billing.preview.columns.volume") }}
                    </th>
                    <th>{{ $t("billing.preview.columns.status") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in certificateRows" :key="row.id">
                    <td>{{ countryName(row.country) }}</td>
                    <td>
                      {{ $t(`billing.preview.technology.${row.technology}`) }}
                    </td>
                    <td>{{ row.vintage }}</td>
                    <td class="text-end">{{ formatMwh(row.volume) }}</td>
                    <td>
                      <span
                        class="badge badge-phoenix"
                        :class="
                          row.status === 'cancelled'
                            ? 'badge-phoenix-success'
                            : 'badge-phoenix-info'
                        "
                      >
                        {{ $t(`billing.preview.status.${row.status}`) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-xl-5">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="mb-3">{{ $t("billing.preview.price_title") }}</h5>
            <div class="chart-box">
              <canvas ref="priceCanvas"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * A dashboard filled with made-up figures, shown behind the billing prompt until the company pays. It makes no
 * request: the preview must look complete without the company's data.
 */
import { useChartCanvas } from "@/admin/financial-dashboard/useChartCanvas";
import { onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

type Technology = "wind" | "solar" | "hydro" | "biomass";

const MONTHLY_CONSUMPTION_MWH = [
  4200, 3900, 4100, 3700, 3500, 3300, 3200, 3400, 3600, 3900, 4100, 4300,
];
const MONTHLY_CERTIFICATES_MWH = [
  3100, 3000, 3500, 3400, 3500, 3300, 3200, 3400, 3500, 3600, 3700, 3800,
];
const TECHNOLOGY_SHARE: Record<Technology, number> = {
  wind: 46,
  hydro: 28,
  solar: 19,
  biomass: 7,
};
const TECHNOLOGY_COLOURS: Record<Technology, string> = {
  wind: "#3874ff",
  hydro: "#25b003",
  solar: "#e5780b",
  biomass: "#7d8ea3",
};
const PRICE_EUR_PER_MWH = [
  0.42, 0.45, 0.48, 0.52, 0.55, 0.61, 0.66, 0.71, 0.74, 0.79, 0.83, 0.88,
];

const certificateRows: Array<{
  id: number;
  country: string;
  technology: Technology;
  vintage: number;
  volume: number;
  status: "cancelled" | "held";
}> = [
  {
    id: 1,
    country: "NO",
    technology: "hydro",
    vintage: 2026,
    volume: 1200,
    status: "cancelled",
  },
  {
    id: 2,
    country: "SE",
    technology: "wind",
    vintage: 2026,
    volume: 950,
    status: "cancelled",
  },
  {
    id: 3,
    country: "EE",
    technology: "wind",
    vintage: 2026,
    volume: 640,
    status: "held",
  },
  {
    id: 4,
    country: "ES",
    technology: "solar",
    vintage: 2026,
    volume: 810,
    status: "held",
  },
  {
    id: 5,
    country: "LT",
    technology: "biomass",
    vintage: 2025,
    volume: 300,
    status: "cancelled",
  },
  {
    id: 6,
    country: "FI",
    technology: "wind",
    vintage: 2026,
    volume: 720,
    status: "held",
  },
];

const { t, locale } = useI18n();
const coverageCanvas = useTemplateRef<HTMLCanvasElement>("coverageCanvas");
const technologyCanvas = useTemplateRef<HTMLCanvasElement>("technologyCanvas");
const priceCanvas = useTemplateRef<HTMLCanvasElement>("priceCanvas");
const coverageChart = useChartCanvas(coverageCanvas);
const technologyChart = useChartCanvas(technologyCanvas);
const priceChart = useChartCanvas(priceCanvas);

const kpis = [
  { key: "held", icon: "layer-group", value: formatMwh(18450) },
  { key: "cancelled", icon: "ban", value: formatMwh(41200) },
  { key: "coverage", icon: "chart-pie", value: "87%" },
  { key: "countries", icon: "globe", value: "6" },
];

// Chart.js draws on the mounted canvases.
onMounted(() => {
  const months = monthLabels();
  coverageChart.render({
    type: "bar",
    data: {
      labels: months,
      datasets: [
        {
          label: t("billing.preview.certificates"),
          data: MONTHLY_CERTIFICATES_MWH,
          backgroundColor: "#3874ff",
          borderRadius: 4,
        },
        {
          label: t("billing.preview.consumption"),
          data: MONTHLY_CONSUMPTION_MWH,
          backgroundColor: "#cbd0dd",
          borderRadius: 4,
        },
      ],
    },
    options: {
      animation: false,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
    },
  });
  const technologies = Object.keys(TECHNOLOGY_SHARE) as Technology[];
  technologyChart.render({
    type: "doughnut",
    data: {
      labels: technologies.map((technology) =>
        t(`billing.preview.technology.${technology}`)
      ),
      datasets: [
        {
          data: technologies.map((technology) => TECHNOLOGY_SHARE[technology]),
          backgroundColor: technologies.map(
            (technology) => TECHNOLOGY_COLOURS[technology]
          ),
        },
      ],
    },
    options: {
      animation: false,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
    },
  });
  priceChart.render({
    type: "line",
    data: {
      labels: months,
      datasets: [
        {
          label: t("billing.preview.price_label"),
          data: PRICE_EUR_PER_MWH,
          borderColor: "#25b003",
          backgroundColor: "rgba(37, 176, 3, 0.12)",
          fill: true,
          tension: 0.35,
          pointRadius: 0,
        },
      ],
    },
    options: {
      animation: false,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
    },
  });
});

function monthLabels(): string[] {
  const format = new Intl.DateTimeFormat(locale.value, { month: "short" });
  return MONTHLY_CONSUMPTION_MWH.map((_, month) =>
    format.format(new Date(2026, month, 1))
  );
}

function countryName(code: string): string {
  return (
    new Intl.DisplayNames([locale.value], { type: "region" }).of(code) ?? code
  );
}

function formatMwh(value: number): string {
  return `${new Intl.NumberFormat(locale.value).format(value)} MWh`;
}
</script>

<style scoped lang="scss">
.chart-box {
  position: relative;
  height: 260px;
}
</style>
